import { useCallback, useRef, useState } from 'react';
import { Form, SimpleItem, RequiredRule, PatternRule, Label } from 'devextreme-react/form';
import { SquirePopup } from '../squire-popup/squire-popup';

// Matches GridLayoutConfiguration's Name column, so an over-long name is refused here rather than by
// the database.
const NAME_MAX_LENGTH = 100;

// DevExtreme's own dropdown and popup overlays both carry role="dialog", so tests need something
// unambiguous to scope to.
const NAME_POPUP_ATTR = { 'data-testid': 'grid-layout-name-popup' };

/**
 * Asks for a layout name. One popup serves both "Save as new layout" and "Rename", which differ only
 * in their title and whether a name is pre-filled.
 *
 * `onSubmit(name)` is awaited and is expected to **throw** when the name is not acceptable — a
 * duplicate name is a rejection from the server, and the popup has to stay open so the user can fix
 * the name they are already looking at. SquirePopup's SaveButton catches the throw, notifies, and
 * leaves the popup up; only a clean resolve closes it.
 *
 * Render this conditionally (`{isOpen && <GridLayoutNamePopup ... />}`) rather than leaving it
 * mounted on `visible={false}` — see the DevExtreme E1010 note on eagerly mounted sub-popups.
 */
export function GridLayoutNamePopup({
  visible = false,
  title,
  initialName = '',
  confirmText = 'Save',
  onSubmit,
  onClose,
}) {
  const formRef = useRef(null);

  // The values state drives the Form; the ref is what handleSave reads. The editor commits its value
  // on blur, which happens during the Save button's mousedown, so by the time onSave runs the state
  // closure can still hold the previous value while the ref is current.
  const [formData] = useState(() => ({ layoutName: initialName }));
  const nameRef = useRef(initialName);

  const handleFieldDataChanged = useCallback((e) => {
    if (e.dataField === 'layoutName') {
      nameRef.current = e.value ?? '';
    }
  }, []);

  const handleSave = useCallback(async () => {
    const formInstance = formRef.current?.instance();
    if (!formInstance) {
      throw new Error('Form instance not found.');
    }

    // Validated here rather than through the Save button's validation group: the Form owns its own
    // validation, and this is the pattern the transition forms already use.
    const validationResult = await formInstance.validate();
    if (!validationResult.isValid) {
      throw new Error('Please enter a layout name.');
    }

    await onSubmit(String(nameRef.current ?? '').trim());
  }, [onSubmit]);

  return (
    <SquirePopup
      visible={visible}
      title={title}
      mode="popup"
      wrapperAttr={NAME_POPUP_ATTR}
      width={420}
      height="auto"
      maxHeight="60vh"
      onSave={handleSave}
      onClose={onClose}
      saveButtonText={confirmText}
    >
      <Form
        ref={formRef}
        formData={formData}
        onFieldDataChanged={handleFieldDataChanged}
        labelLocation="top"
        validationMessageMode="always"
      >
        <SimpleItem
          dataField="layoutName"
          editorType="dxTextBox"
          editorOptions={{
            placeholder: 'Layout name',
            maxLength: NAME_MAX_LENGTH,
            showClearButton: true,
          }}
        >
          <Label text="Name" />
          <RequiredRule message="A layout name is required" />
          <PatternRule
            pattern={/\S/}
            message="A layout name cannot be only spaces"
          />
        </SimpleItem>
      </Form>
    </SquirePopup>
  );
}
